// Phase I0.3 — Badge primitive.
// 6 variants: amber | success | danger | warn | info | ghost.
// JetBrains Mono 10px uppercase, padded 3×8, radius 2.

import type { CSSProperties, ReactNode } from "react";

export type BadgeVariant = "amber" | "success" | "danger" | "warn" | "info" | "ghost";

export interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  style?: CSSProperties;
}

interface VariantSpec {
  bg: string;
  color: string;
  border?: string;
}

const VARIANTS: Record<BadgeVariant, VariantSpec> = {
  amber: { bg: "var(--sa-amber-glow)", color: "var(--sa-accent-saffron)" },
  success: { bg: "var(--sa-signal-green-dim)", color: "var(--sa-signal-green)" },
  danger: { bg: "var(--sa-signal-red-dim)", color: "var(--sa-signal-red)" },
  warn: { bg: "var(--sa-signal-warn-dim)", color: "var(--sa-signal-warn)" },
  info: { bg: "var(--sa-signal-info-dim)", color: "var(--sa-signal-info)" },
  ghost: {
    bg: "transparent",
    color: "var(--sa-fg-muted)",
    border: "1px solid var(--sa-hairline)",
  },
};

export function Badge({ children, variant = "amber", style }: BadgeProps) {
  const v = VARIANTS[variant];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--sa-font-mono)",
        fontSize: 10,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        padding: "3px 8px",
        borderRadius: 2,
        fontWeight: 500,
        background: v.bg,
        color: v.color,
        border: v.border ?? "none",
        ...style,
      }}
    >
      {children}
    </span>
  );
}
